import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgentProfile({ currentUserId, appMode }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    identityNumber: '',
    registrationNumber: '',
    agencyName: '',
    agencyAddress: '',
    city: '',
    postalCode: '',
    certificatesUrl: '',
    idDocumentUrl: ''
  });

  useEffect(() => {
    // Only agents can access this page
    if (appMode !== 'Agent') {
      toast.error('Only agents can access this page');
      navigate('/dashboard');
      return;
    }

    fetchProfile();
  }, [currentUserId, appMode, navigate]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/users/${currentUserId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data);
      setFormData({
        fullName: response.data.fullName || '',
        identityNumber: response.data.identityNumber || '',
        registrationNumber: response.data.registrationNumber || '',
        agencyName: response.data.agencyName || '',
        agencyAddress: response.data.agencyAddress || '',
        city: response.data.city || '',
        postalCode: response.data.postalCode || '',
        certificatesUrl: response.data.certificatesUrl || '',
        idDocumentUrl: response.data.idDocumentUrl || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, [fieldName]: reader.result });
      toast.success('Document uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fullName || !formData.identityNumber || !formData.registrationNumber || 
        !formData.agencyName || !formData.agencyAddress || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.certificatesUrl || !formData.idDocumentUrl) {
      toast.error('Please upload both required documents');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/users/${currentUserId}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profile updated successfully! Awaiting admin verification.');
      fetchProfile(); // Refresh profile
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      default:
        return '⏳';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Agent Profile</h1>
        <p className="text-gray-600">Complete your profile and upload documents for verification</p>
      </div>

      {/* Verification Status Card */}
      <div className={`mb-6 p-6 rounded-lg border-2 ${getStatusColor(profile?.verificationStatus)}`}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">{getStatusIcon(profile?.verificationStatus)}</span>
          <div>
            <h3 className="text-lg font-bold capitalize">
              Verification Status: {profile?.verificationStatus}
            </h3>
            {profile?.verificationStatus === 'pending' && (
              <p className="text-sm">Your profile is under review by admin</p>
            )}
            {profile?.verificationStatus === 'approved' && (
              <p className="text-sm">Your profile has been verified and approved</p>
            )}
            {profile?.verificationStatus === 'rejected' && (
              <p className="text-sm">Your profile was rejected. Please review and resubmit.</p>
            )}
          </div>
        </div>
        
        {profile?.verificationNotes && (
          <div className="mt-4 p-4 bg-white bg-opacity-50 rounded">
            <p className="font-semibold mb-1">Admin Notes:</p>
            <p className="text-sm">{profile.verificationNotes}</p>
          </div>
        )}
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">
        {/* Personal Information */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.identityNumber}
                onChange={(e) => setFormData({ ...formData, identityNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="8501015800080"
              />
            </div>
          </div>
        </div>

        {/* Agency Information */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b">Agency Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agency Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.agencyName}
                onChange={(e) => setFormData({ ...formData, agencyName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="ABC Real Estate"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.registrationNumber}
                onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="REG123456"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Johannesburg"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agency Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.agencyAddress}
                onChange={(e) => setFormData({ ...formData, agencyAddress: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="123 Main Street, Sandton"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code
              </label>
              <input
                type="text"
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="2196"
              />
            </div>
          </div>
        </div>

        {/* Document Uploads */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b">Required Documents</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Real Estate Agent Certificate <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e, 'certificatesUrl')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
              {formData.certificatesUrl && (
                <p className="text-sm text-green-600 mt-1">✓ Certificate uploaded</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Upload your real estate agent certificate (PDF, JPG, or PNG, max 5MB)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Identification Document (ID/Passport) <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e, 'idDocumentUrl')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
              {formData.idDocumentUrl && (
                <p className="text-sm text-green-600 mt-1">✓ ID document uploaded</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Upload a copy of your ID or passport (PDF, JPG, or PNG, max 5MB)
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Submit for Verification'}
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>ℹ️ Note:</strong> After submitting, your profile will be reviewed by an administrator. 
            You can edit and resubmit your profile at any time.
          </p>
        </div>
      </form>
    </div>
  );
}
